<template>
  <div class="login-page">
    <b-container class="login-shell">
      <b-row class="login-frame no-gutters">
        <b-col lg="5" class="d-none d-lg-flex">
          <section class="brand-panel">
            <p class="eyebrow">User Signup</p>
            <h1>Create your account and get started in a few simple steps.</h1>
            <p class="brand-copy">
              Set up your profile, confirm your details, and sign in to access the platform with a
              personal account made for everyday use.
            </p>

            <div class="brand-metrics">
              <div class="metric-card">
                <span class="metric-value">Simple</span>
                <span class="metric-label">
                  Only the essential details are required to create your account.
                </span>
              </div>
              <div class="metric-card">
                <span class="metric-value">Personal</span>
                <span class="metric-label">
                  Your account details and password are verified before signup is completed.
                </span>
              </div>
            </div>
          </section>
        </b-col>

        <b-col cols="12" lg="7">
          <section class="form-panel">
            <div class="form-panel__inner">
              <div class="mobile-brand d-lg-none">
                <p class="eyebrow mb-2">HPPDM Workspace</p>
                <h1>Create account</h1>
              </div>

              <div class="form-header">
                <p class="form-kicker">Sign up</p>
                <h2>Create your account</h2>
                <p class="form-subtitle">Fill in your details below to create a new account.</p>
              </div>

              <div v-if="submitError" class="status-alert status-alert--danger" role="alert">
                {{ submitError }}
              </div>

              <div v-if="showAlert" class="status-alert status-alert--success" role="alert">
                Your account has been created successfully. Please check your email for the
                activation link.
              </div>

              <ValidationObserver ref="signupObserver">
                <form class="login-form" @submit.prevent="onSubmit">
                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    name="Username"
                    rules="required|min:3"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="signup-username">
                        Username
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">Choose the username you will use to sign in.</p>
                      <b-form-input
                        id="signup-username"
                        v-model.trim="form.username"
                        :class="fieldClasses(errors)"
                        placeholder="Enter your username"
                        autocomplete="username"
                        v-bind="ariaInput"
                      />
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    name="Email address"
                    rules="required|email"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="signup-email">
                        Email address
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">
                        Use a valid email address for account activation.
                      </p>
                      <b-form-input
                        id="signup-email"
                        v-model.trim="form.email"
                        :class="fieldClasses(errors)"
                        type="email"
                        placeholder="name@organization.org"
                        autocomplete="email"
                        v-bind="ariaInput"
                      />
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    name="Address"
                    rules="required|min:5"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="signup-address">
                        Address
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">Provide the address linked to this account.</p>
                      <b-form-input
                        id="signup-address"
                        v-model.trim="form.address"
                        :class="fieldClasses(errors)"
                        placeholder="Enter your address"
                        autocomplete="street-address"
                        v-bind="ariaInput"
                      />
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    vid="signup-password"
                    name="Password"
                    rules="required|min:8"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="signup-password">
                        Password
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">
                        Use at least 8 characters for a stronger password.
                      </p>
                      <div class="field-input-wrap">
                        <b-form-input
                          id="signup-password"
                          v-model="form.password"
                          :class="[fieldClasses(errors), 'premium-input--with-toggle']"
                          :type="showPassword ? 'text' : 'password'"
                          placeholder="Create a password"
                          autocomplete="new-password"
                          v-bind="ariaInput"
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          :aria-label="showPassword ? 'Hide password' : 'Show password'"
                          :title="showPassword ? 'Hide password' : 'Show password'"
                          @click="showPassword = !showPassword"
                        >
                          <i
                            :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                            aria-hidden="true"
                          ></i>
                        </button>
                      </div>
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    name="Password confirmation"
                    rules="required|confirmed:signup-password"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="signup-confirm-password">
                        Confirm password
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">Re-enter your password to confirm it matches.</p>
                      <div class="field-input-wrap">
                        <b-form-input
                          id="signup-confirm-password"
                          v-model="form.confirm_password"
                          :class="[fieldClasses(errors), 'premium-input--with-toggle']"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          placeholder="Confirm your password"
                          autocomplete="new-password"
                          v-bind="ariaInput"
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          :aria-label="
                            showConfirmPassword
                              ? 'Hide confirmation password'
                              : 'Show confirmation password'
                          "
                          :title="
                            showConfirmPassword
                              ? 'Hide confirmation password'
                              : 'Show confirmation password'
                          "
                          @click="showConfirmPassword = !showConfirmPassword"
                        >
                          <i
                            :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                            aria-hidden="true"
                          ></i>
                        </button>
                      </div>
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <label class="terms-check">
                    <input
                      v-model="acceptedConfirmation"
                      :class="{
                        'terms-check__input--error': submitAttempted && !acceptedConfirmation,
                      }"
                      type="checkbox"
                    />
                    <span>I confirm the signup details above are correct.</span>
                  </label>

                  <button type="submit" class="login-button" :disabled="loading">
                    <b-spinner v-show="loading" small class="mr-2" />
                    <span>{{ loading ? 'Creating account...' : 'Create account' }}</span>
                  </button>

                  <div class="auth-switch">
                    <span class="mr-1">Already have an account?</span>
                    <b-link class="text-decoration-none" to="/auth/login">Log in</b-link>
                  </div>
                </form>
              </ValidationObserver>
            </div>
          </section>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  data() {
    return {
      form: {
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        address: '',
      },
      acceptedConfirmation: false,
      showAlert: false,
      submitError: '',
      loading: false,
      submitAttempted: false,
      showPassword: false,
      showConfirmPassword: false,
    };
  },

  methods: {
    ...mapActions('auth', ['REGISTRATION']),

    fieldClasses(errors) {
      return {
        'premium-input': true,
        'premium-input--error': this.shouldShowFieldError(errors),
      };
    },

    shouldShowFieldError(errors) {
      return this.submitAttempted && Boolean(errors.length);
    },

    resetForm() {
      this.form = {
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        address: '',
      };
      this.acceptedConfirmation = false;
      this.submitError = '';
      this.submitAttempted = false;
      this.showPassword = false;
      this.showConfirmPassword = false;

      if (this.$refs.signupObserver) {
        this.$refs.signupObserver.reset();
      }
    },

    extractErrorMessage(error) {
      const responseData = error?.response?.data;

      if (typeof responseData === 'string') {
        return responseData;
      }

      if (responseData?.error) {
        return responseData.error;
      }

      if (responseData?.detail) {
        return responseData.detail;
      }

      if (responseData && typeof responseData === 'object') {
        const firstMessage = Object.values(responseData)
          .flat()
          .find((value) => typeof value === 'string');

        if (firstMessage) {
          return firstMessage;
        }
      }

      return 'We could not create your account right now. Please review your details and try again.';
    },

    async onSubmit() {
      this.submitAttempted = true;
      this.submitError = '';
      this.showAlert = false;

      const isValid = await this.$refs.signupObserver.validate();

      if (!isValid) {
        this.submitError = 'Please correct the highlighted fields and try again.';
        return;
      }

      if (!this.acceptedConfirmation) {
        this.submitError = 'Please confirm that your signup details are correct before continuing.';
        this.$toast.show({
          type: 'ERROR',
          message: this.submitError,
        });
        return;
      }

      this.loading = true;

      try {
        const payload = {
          username: this.form.username.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          address: this.form.address.trim(),
        };

        const { status } = await this.REGISTRATION(payload);

        if (status === 200 || status === 201) {
          this.showAlert = true;
          this.$toast.show({
            type: 'SUCCESS',
            message:
              'Your account has been created successfully. Please check your email for the activation link.',
          });
          this.resetForm();

          setTimeout(() => {
            this.$router.push('/auth/login');
          }, 3000);
          return;
        }

        this.submitError = 'Signup did not complete successfully. Please try again.';
      } catch (error) {
        this.submitError = this.extractErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  padding: 1.5rem 0;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.2), transparent 28%),
    radial-gradient(circle at bottom right, rgba(13, 34, 20, 0.18), transparent 30%),
    linear-gradient(135deg, #f3f8f3 0%, #edf3ee 45%, #e4ece5 100%);
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.26) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.26) 1px, transparent 1px);
  background-size: 120px 120px;
  opacity: 0.45;
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 3rem);
}

.login-frame {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 30px 90px rgba(20, 43, 27, 0.15);
  backdrop-filter: blur(14px);
}

.brand-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 3.15rem 2.6rem;
  color: #f8fff8;
  background: linear-gradient(180deg, rgba(5, 20, 10, 0.12), rgba(5, 20, 10, 0.2)),
    linear-gradient(155deg, #0d3418 0%, #195426 52%, #30ab4c 100%);
}

.brand-panel::after {
  content: '';
  position: absolute;
  width: 14rem;
  height: 14rem;
  right: -3rem;
  bottom: -4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.eyebrow {
  margin-bottom: 1rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.76rem;
  font-weight: 600;
  opacity: 0.84;
}

.brand-panel h1,
.mobile-brand h1,
.form-header h2 {
  font-family: Georgia, 'Times New Roman', serif;
}

.brand-panel h1 {
  position: relative;
  z-index: 1;
  margin-bottom: 1.25rem;
  font-size: clamp(2.2rem, 3vw, 3.2rem);
  line-height: 1.08;
}

.brand-copy {
  position: relative;
  z-index: 1;
  max-width: 26rem;
  margin-bottom: 1.7rem;
  font-size: 0.98rem;
  line-height: 1.7;
  color: rgba(248, 255, 248, 0.82);
}

.brand-metrics {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.95rem;
}

.metric-card {
  padding: 1.05rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
}

.metric-value {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 1rem;
  font-weight: 700;
}

.metric-label {
  display: block;
  font-size: 0.92rem;
  line-height: 1.5;
  color: rgba(248, 255, 248, 0.82);
}

.form-panel {
  display: flex;
  align-items: center;
  min-height: 100%;
  padding: 2.5rem 1.9rem;
}

.form-panel__inner {
  width: 100%;
  max-width: 33rem;
  margin: 0 auto;
}

.form-header {
  margin-bottom: 0.75rem;
}

.form-kicker {
  margin-bottom: 0.55rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #1f6b31;
}

.form-header h2 {
  margin-bottom: 0.55rem;
  font-size: clamp(1.9rem, 3vw, 2.55rem);
  color: #0d2c16;
}

.form-subtitle {
  margin-bottom: 0;
  font-size: 0.96rem;
  line-height: 1.6;
  color: #5b6b5f;
}

.status-alert {
  position: relative;
  margin-top: 0.85rem;
  margin-bottom: 0.35rem;
  padding: 0.9rem 1rem;
  border: 1px solid transparent;
  border-radius: 14px;
  font-size: 0.95rem;
  line-height: 1.45;
}

.status-alert--danger {
  color: #8d1f16;
  background: #f8d7da;
  border-color: #f1b9bf;
}

.status-alert--success {
  color: #155724;
  background: #d4edda;
  border-color: #b7dfc2;
}

.login-form {
  margin-top: 0.45rem;
}

.premium-field {
  margin-top: 0.95rem;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.28rem;
  font-size: 0.94rem;
  font-weight: 700;
  color: #13311b;
}

.required-mark {
  color: #d93025;
}

.field-description {
  margin-bottom: 0.45rem;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #647468;
}

.field-input-wrap {
  position: relative;
}

.premium-input {
  min-height: 3.25rem;
  padding: 0.82rem 0.95rem;
  border: 1px solid rgba(16, 46, 25, 0.14);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 251, 247, 0.98));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 10px 24px rgba(13, 44, 22, 0.06);
  color: #13311b;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.premium-input--with-toggle {
  padding-right: 3.25rem;
}

.premium-input::placeholder {
  color: #91a092;
}

.premium-input:focus {
  border-color: #2b8a43;
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.16), 0 18px 34px rgba(13, 44, 22, 0.08);
  transform: translateY(-1px);
}

.premium-input--error,
.premium-input--error:focus {
  border-color: #d93025;
  box-shadow: 0 0 0 4px rgba(217, 48, 37, 0.14);
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  background: rgba(31, 107, 49, 0.08);
  font-size: 0.92rem;
  color: #2a6a39;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  transform: translateY(-50%);
}

.password-toggle:hover {
  background: rgba(31, 107, 49, 0.14);
  color: #154d23;
}

.password-toggle:focus {
  outline: none;
  border-color: rgba(48, 171, 76, 0.28);
  background: rgba(31, 107, 49, 0.14);
  color: #154d23;
}

.password-toggle i {
  line-height: 1;
}

.field-error {
  margin-top: 0.38rem;
  margin-bottom: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #d93025;
}

.terms-check {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #324638;
}

.terms-check input {
  width: 1rem;
  height: 1rem;
  margin-top: 0.2rem;
  accent-color: #1f6b31;
}

.terms-check__input--error {
  outline: 2px solid rgba(217, 48, 37, 0.35);
  outline-offset: 1px;
}

.login-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.25rem;
  margin-top: 0.65rem;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  color: #f7fff8;
  background: linear-gradient(135deg, #1d6d31 0%, #30ab4c 100%);
  box-shadow: 0 18px 36px rgba(31, 107, 49, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-button:hover:not(:disabled),
.login-button:focus:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 22px 40px rgba(31, 107, 49, 0.28);
}

.login-button:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.auth-switch {
  margin-top: 0.9rem;
  text-align: center;
  color: #5b6b5f;
}

.auth-switch a {
  font-weight: 700;
  color: #1f6b31;
}

@media (max-width: 991.98px) {
  .login-page {
    padding: 1rem 0 2rem;
  }

  .login-shell {
    min-height: auto;
  }

  .form-panel {
    padding: 2.1rem 1.25rem;
  }

  .mobile-brand {
    margin-bottom: 1rem;
  }

  .mobile-brand h1 {
    margin-bottom: 0;
    font-size: 2rem;
    color: #0d2c16;
  }
}

@media (max-width: 575.98px) {
  .login-frame {
    border-radius: 22px;
  }

  .form-panel {
    padding: 1.9rem 1rem;
  }

  .form-header h2 {
    font-size: 1.85rem;
  }
}
</style>
